/**
 * DemoUpgradeCard — Batch #14.203 (refondu).
 *
 * Carte CTA affichée uniquement en mode démo dans le PortalDashboard.
 * Un seul bouton "Devenir client" ouvre un mini-formulaire en modale.
 * À la soumission : POST public /api/public/demo-lead → tuile Leads admin.
 */
import { useState } from "react";
import { Sparkles, X, ArrowRight, Send, Loader2, CheckCircle2, Video, Users } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

const SECTEURS = [
  "Particulier (déclaration de revenus)",
  "Travailleur autonome / pigiste (T777)",
  "Propriétaire immobilier (RL-31, baux)",
  "Petite entreprise / TPS-TVQ",
  "Étudiant",
  "Retraité",
  "Autre — voir la note",
];

const METHODES = [
  { value: "email",     label: "Courriel" },
  { value: "telephone", label: "Téléphone" },
  { value: "sms",       label: "SMS / texto" },
];

export default function DemoUpgradeCard() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    prenom: "", nom: "", telephone: "", courriel: "",
    adresse: "", methode_contact: "email", moment_contact: "",
    rdv_demande: false, rdv_format: "meet",
    secteur_expertise: "", note: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    // Validation locale
    if (!form.prenom.trim() || !form.nom.trim() || !form.telephone.trim() || !form.courriel.trim()) {
      toast.error("Prénom, Nom, Téléphone et Courriel sont obligatoires.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/demo-lead", {
        prenom: form.prenom,
        nom: form.nom,
        telephone: form.telephone,
        courriel: form.courriel,
        adresse: form.adresse || "",
        methode_contact: form.methode_contact,
        moment_contact: form.moment_contact || "",
        rdv_demande: !!form.rdv_demande,
        rdv_format: form.rdv_demande ? form.rdv_format : null,
        secteur_expertise: form.secteur_expertise || "",
        note: form.note || "",
        website: form.website || "",
      });
      setDone(true);
      toast.success("Demande envoyée — l'équipe DG Comptabilité vous contacte rapidement.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : (detail || "Erreur d'envoi");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const closeAndReset = () => {
    setOpen(false);
    setDone(false);
    setForm({
      prenom: "", nom: "", telephone: "", courriel: "",
      adresse: "", methode_contact: "email", moment_contact: "",
      rdv_demande: false, rdv_format: "meet",
      secteur_expertise: "", note: "",
    });
  };

  return (
    <>
      {/* Bandeau promotionnel ─ un seul bouton "Devenir client" */}
      <div
        className="relative overflow-hidden rounded-2xl mb-5 sm:mb-6 shadow-lg"
        style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 45%, #06b6d4 100%)" }}
        data-testid="demo-upgrade-card"
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-5 sm:px-7 py-5 sm:py-6">
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-yellow-300" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-yellow-300/90 text-amber-900 text-[10px] font-bold uppercase tracking-wider mb-1.5">
                <Sparkles className="w-3 h-3" /> Vous êtes en mode démo
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">
                Envie d'arrêter de jouer et de simplifier vraiment votre fiscalité ?
              </h2>
              <p className="text-sm text-blue-50/95 mt-1 leading-relaxed">
                Tout ce que vous voyez ici fonctionne pour vrai chez nos clients : déclarations,
                gestion d'immeubles, paiements, communications tracées, signature TAL.
                Remplissez le formulaire — un expert vous contacte avec un plan personnalisé,
                <b> sans frais ni engagement</b>.
              </p>

              <button
                onClick={() => setOpen(true)}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-blue-700 font-semibold text-sm shadow hover:shadow-lg hover:bg-blue-50 transition"
                data-testid="demo-upgrade-open-form-btn"
              >
                Devenir client <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale formulaire */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) closeAndReset(); }}
          data-testid="demo-upgrade-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4 max-h-[94vh] overflow-y-auto">
            {/* Header */}
            <div
              className="sticky top-0 z-10 px-5 sm:px-7 py-4 flex items-center justify-between text-white"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)" }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <h3 className="text-base sm:text-lg font-bold">Devenir client DG Comptabilité</h3>
              </div>
              <button
                onClick={closeAndReset}
                disabled={busy}
                className="p-1.5 rounded-lg hover:bg-white/15 transition disabled:opacity-40"
                aria-label="Fermer"
                data-testid="demo-upgrade-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 sm:px-7 py-5">
              {done ? (
                <div className="text-center py-6" data-testid="demo-upgrade-success">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-800">Merci !</h4>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-md mx-auto">
                    Nous avons bien reçu votre demande. Un membre de l'équipe DG Comptabilité
                    vous contactera très bientôt par votre <b>méthode préférée</b>.
                  </p>
                  <button
                    onClick={closeAndReset}
                    className="mt-5 px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700"
                    data-testid="demo-upgrade-done-close"
                  >
                    Fermer
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  {/* Pot de miel anti-bot : invisible, doit rester vide */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website || ""}
                    onChange={(e) => setForm({ ...form, website: e.target.value })} className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" data-testid="demo-lead-website" />
                  <p className="text-xs text-slate-500">
                    Vos coordonnées restent confidentielles. Aucun engagement à ce stade.
                  </p>

                  {/* Identité */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FieldText label="Prénom *" value={form.prenom}     onChange={(v) => set("prenom", v)} testid="demo-lead-prenom" required />
                    <FieldText label="Nom *"    value={form.nom}        onChange={(v) => set("nom", v)}    testid="demo-lead-nom" required />
                    <FieldText label="Téléphone *" value={form.telephone} onChange={(v) => set("telephone", v)} testid="demo-lead-tel" type="tel" placeholder="+14185551234" required />
                    <FieldText label="Courriel *"  value={form.courriel}  onChange={(v) => set("courriel", v)}  testid="demo-lead-email" type="email" required />
                  </div>

                  <FieldText label="Adresse" value={form.adresse} onChange={(v) => set("adresse", v)} testid="demo-lead-adresse" placeholder="123 rue Principale, ville, code postal" />

                  {/* Contact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Méthode de contact préférée *</Label>
                      <select
                        value={form.methode_contact}
                        onChange={(e) => set("methode_contact", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        data-testid="demo-lead-methode"
                      >
                        {METHODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <FieldText label="Moment préféré pour être contacté" value={form.moment_contact} onChange={(v) => set("moment_contact", v)} testid="demo-lead-moment" placeholder="ex: matinée, soir, week-end" />
                  </div>

                  {/* RDV */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.rdv_demande}
                        onChange={(e) => set("rdv_demande", e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-blue-600"
                        data-testid="demo-lead-rdv-check"
                      />
                      <span className="text-sm font-medium text-slate-800">
                        Je souhaite un <b>rendez-vous gratuit</b> pour monter un plan, recevoir une soumission et de l'information.
                      </span>
                    </label>
                    {form.rdv_demande && (
                      <div className="mt-3 pl-6 flex gap-3">
                        <button
                          type="button"
                          onClick={() => set("rdv_format", "presentiel")}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm border-2 transition ${
                            form.rdv_format === "presentiel"
                              ? "border-blue-500 bg-blue-100 text-blue-800 font-semibold"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          data-testid="demo-lead-rdv-presentiel"
                        >
                          <Users className="w-4 h-4" /> En présence
                        </button>
                        <button
                          type="button"
                          onClick={() => set("rdv_format", "meet")}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm border-2 transition ${
                            form.rdv_format === "meet"
                              ? "border-blue-500 bg-blue-100 text-blue-800 font-semibold"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                          data-testid="demo-lead-rdv-meet"
                        >
                          <Video className="w-4 h-4" /> Google Meet
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Secteur */}
                  <div>
                    <Label>Secteur d'expertise recherché</Label>
                    <select
                      value={form.secteur_expertise}
                      onChange={(e) => set("secteur_expertise", e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      data-testid="demo-lead-secteur"
                    >
                      <option value="">— Sélectionnez —</option>
                      {SECTEURS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Note */}
                  <div>
                    <Label>Note (situation particulière, questions, contexte…)</Label>
                    <textarea
                      value={form.note}
                      onChange={(e) => set("note", e.target.value)}
                      rows={3}
                      maxLength={2000}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                      placeholder="Optionnel — ce qui pourrait nous aider à mieux vous servir"
                      data-testid="demo-lead-note"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={closeAndReset}
                      disabled={busy}
                      className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                      data-testid="demo-lead-cancel"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold text-sm shadow hover:bg-blue-700 transition disabled:opacity-50"
                      data-testid="demo-lead-submit"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Envoyer ma demande
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Petits champs réutilisables ──────────────────────────────────────────
function Label({ children }) {
  return <span className="block text-xs font-medium text-slate-700 mb-1">{children}</span>;
}

function FieldText({ label, value, onChange, testid, type = "text", placeholder, required }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        data-testid={testid}
      />
    </label>
  );
}
